/**
 * WPQ-NPCIL LINKING COMPONENT
 * 
 * PURPOSE:
 * Links Welder Performance Qualification (WPQ) records with NPCIL project joints
 * for nuclear welding compliance and traceability.
 * 
 * DYNAMODB STRUCTURE:
 * 
 * 1. WI_mst Table - Main Linking Records:
 *    PK: "NpcilWpqLinking"
 *    SK: "{ProjectNo}#{JointType}#SeqNo#{SequenceNumber}"
 *    Attributes:
 *    - SeqNo: Sequence number for grouping
 *    - WI_Project_No: NPCIL project identifier
 *    - WI_Joint_No: Comma-separated joint numbers ("J001,J002,J003")
 *    - WI_Joint_Type: "Groove", "Overlay", or "Tubesheet"
 *    - WI_Welder_Id: Qualified welder identifier (display only)
 *    - WI_Wpq_Radio: "Link WPQ with Project" or "Linking WPQ with project not Required"
 * 
 * 2. WI_mst Table - Individual WPQ Records:
 *    PK: "NpcilWpqLinking"
 *    SK: "{ProjectNo}#{JointType}#WPQ#{SequenceNumber}"
 *    Attributes:
 *    - WPQ_WelderId: Individual welder identifier
 *    - WPQ_RecordNo: Specific WPQ qualification number
 *    - NpcilLinking_SK: Reference to main linking record
 *    - Link_Status: Active/Inactive status
 * 
 * 3. WPQ_HEADER Table (Referenced):
 *    PK: "WPQ_HEADER"
 *    SK: "{WelderID}#{WPQRecordNo}"
 *    Contains welder qualification details
 * 
 * BUSINESS LOGIC:
 * 
 * 1. JOINT GROUPING STRATEGY:
 *    - Multiple joints with same welder+joint_type = single record
 *    - Joints stored as comma-separated string for efficiency
 *    - SeqNo groups related joint combinations
 *    
 *    SEQUENCE NUMBER LOGIC:
 *    - SeqNo creates logical groupings within project+joint_type combinations
 *    - Auto-incremented: maxSeqNo = Math.max(...existing.map(ele => ele.SeqNo || 1), 1)
 *    - New groups get: maxSeqNo + 1
 *    - Merged records retain existing SeqNo for consistency
 *    
 *    SEQUENCE EXAMPLES:
 *    Project NPCIL2024, Groove joints:
 *    - SeqNo 1: [W001,W002] → [J001,J002] (First team)
 *    - SeqNo 2: [W003,W004] → [J003,J004] (Second team)
 *    - SeqNo 3: [W001,W002] → [J005,J006] (First team, different joints)
 *    
 *    SK PATTERN WITH SEQUENCE:
 *    - "NPCIL2024#Groove#SeqNo#1" (First combination)
 *    - "NPCIL2024#Groove#SeqNo#2" (Second combination)
 *    - "NPCIL2024#Overlay#SeqNo#1" (Different joint type, sequence resets)
 *    
 *    SEQUENCE BENEFITS:
 *    - Enables multiple welder teams per project+joint_type
 *    - Maintains chronological order of assignments
 *    - Supports efficient range queries by sequence
 *    - Prevents SK collisions in concurrent operations
 *    
 *    WELDER MATCHING LOGIC (EXACT SET COMPARISON):
 *    - Existing Record: Welders [W1, W2] for Groove joints [J001, J002]
 *    - New Request: Welders [W1, W2] for Groove joints [J003, J004]
 *    - Result: MERGE → [W1, W2] for joints [J001, J002, J003, J004]
 *    
 *    - Existing Record: Welders [W1, W2] for Groove joints [J001, J002]
 *    - New Request: Welders [W1, W2, W3, W4] for Groove joints [J003, J004]
 *    - Result: NO MERGE → Different welder sets, create separate record
 *    
 *    MERGE CONDITIONS (ALL MUST BE TRUE):
 *    - Identical joint type (Groove, Overlay, Tubesheet)
 *    - Exact welder set match (same count + same welders)
 *    - Same project number
 *    - No joint number conflicts
 *    
 *    MERGE BENEFITS:
 *    - Reduces DynamoDB item count and storage costs
 *    - Improves query performance for joint lookups
 *    - Maintains logical grouping of related work
 *    - Simplifies reporting and audit trails
 * 
 * 2. CONFLICT RESOLUTION:
 *    - Prevents duplicate joint assignments across different welders
 *    - Shows confirmation for conflicting assignments
 *    
 *    COMPATIBLE JOINT MERGING:
 *    - Joints with identical welder+joint_type are automatically merged
 *    - Example: Welder W001 has Groove joints [J001,J002] + new [J003,J004] = [J001,J002,J003,J004]
 *    - Merging reduces DynamoDB records and improves query performance
 *    - Original record is deleted, new consolidated record created with same SeqNo
 *    - Maintains referential integrity through cascade operations
 *    - Prevents data fragmentation across multiple records
 *    - Optimizes storage by grouping related joints under single SK
 * 
 * 3. NPCIL COMPLIANCE:
 *    - Enforces nuclear industry welding standards
 *    - Maintains welder-to-joint traceability
 *    - Supports regulatory audit requirements
 * 
 * INTERFACE OPERATIONS:
 * - Add: Create new WPQ-joint linkages
 * - Edit: Modify existing linkages with conflict checking
 * - Delete: Remove linkages with cascade handling
 * - View: Display linked WPQ records in grid
 */

import { useState, useRef, forwardRef, useEffect } from "react";
import { Row, Col, Container, Form, Button } from "react-bootstrap";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import { showSuccess, showWarning } from "../utility/ui/notifications/Toaster";
import { getNpcilByPKSK, addNpcilWpqLink, deleteNpcilWpqLink, getWpqNpcilLinkByProjectNumber } from "../../Services/WI-services";
import { getAllProjectsByFlag } from "../../Services/project-services";
import { isAlphanumericWithDot } from "../../functions/validations";
import { BiSelectMultiple } from "react-icons/bi";
import { RenderIf } from "../../functions/welding-functions";
import GridMaster from "../utility/grid/GridMaster";
import MultiValuePopUp from "../utility/ui/modals/MultiValuePopUp";
import { Action } from "../utility/common/constants/CommonConstants"
import { useLocation } from "react-router-dom";
import ConfirmAlertModal from "../Modals/confirmAlertModal";
import { SearchSelect } from "../utility/forms/inputs/search-select";
import { getLinkedWPQRecords, addWPQLinkingData, getWPQLinkingData } from "./WPQLinkingCommon";
import { GridLoader } from "react-spinners";
import { Jointstatus } from "../utility/common/TransactionComponents/TransactionEntity";

const WpqNpcilLinkingAddEdit = forwardRef((props) => {
  const npcilflag = true;
  const [reload, setReload] = useState(false);
  const [projectDetails, setProjectDetails] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [columnDefs, setColumnDefs] = useState([]);
  const childRef = useRef();
  const location = useLocation();
  const action = location.state?.action;
  const [filteredResult, setFilteredResult] = useState([]);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [wpqJointCombinations, setWpqJointCombinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWPQData, setSelectedWPQData] = useState([]);
  console.log("location.state?.data ::", location.state?.data);

  /**
   * NPCIL WPQ LINKING DATA STRUCTURE
   * 
   * DYNAMODB RECORD FORMAT:
   * {
   *   PK: "NpcilWpqLinking",                    // Partition Key
   *   SK: "{ProjectNo}#{JointType}#SeqNo#{Seq}", // Sort Key Pattern
   *   SeqNo: 1,                                   // Grouping sequence
   *   WI_Project_No: "NPCIL2024",                // NPCIL project code
   *   WI_Joint_No: "J001,J002,J003",             // Comma-separated joints
   *   WI_Joint_Type: "Groove",                   // Joint classification
   *   WI_Welder_Id: "W001",                      // Qualified welder
   *   WI_Wpq_Radio: "Link WPQ with Project"      // Linking requirement
   * }
   */
  const [wiObj, setWiObj] = useState(
    {
      PK: "NpcilWpqLinking",
      SK: "",
      SeqNo: "",
      SR_No: "",
      Action: "",
      WI_Project_No: "",
      WI_Joint_No: "",
      WI_WCS_Data: [],
      WI_Welder_Id: "",
      WI_FileFormat: "",
      WI_Wpq_Radio: "",
      WI_Joint_Type: "",
      WCS_Project_No: ""
    }
  );

  let tmpcolumnDefs;
  tmpcolumnDefs = [
    {
      headerName: "Welder Id",
      field: "WPQ_WelderId",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Welder Name",
      field: "WPQ_WelderName",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "WPQ Number",
      field: "WPQ_RecordNo",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }
  ];

  const headerName = (field) => {
    let name = getHeader_FieldName(field, tmpcolumnDefs, "getHeader");
    return name;
  };

  const fieldName = (header) => {
    let name = getHeader_FieldName(header, tmpcolumnDefs, "getField");
    return name;
  };

  const [error, setError] = useState({
    WI_Project_No: {},
    WI_Joint_No: {},
    WI_Welder_Id: {},
    WI_From_Date: {},
    WI_To_Date: {},
    WI_Slip_Status: {},
  });

  const WPQ_Linking_Radio = {
    Linked: "Link WPQ with Project",
    UnLinked: "Linking WPQ with project not Required",
  };

  const fetchProject = async () => {
    const result = await getAllProjectsByFlag(npcilflag);
    setProjectDetails(result);
  };

  const fetchNpcilByPKSK = async () => {
    const result = await getNpcilByPKSK(location.state?.data);
    let linkedWpq = await getWPQLinkingData(result[0]);
    //fetch wpq header data for side grid to display wpq which are already linked
    if (linkedWpq?.length > 0) {
      let linkedData = await getLinkedWPQRecords(linkedWpq.join(","), result[0].WI_Joint_Type);
      if (linkedData) {
        setFilteredResult(linkedData);
        let skValuesArr = linkedData.map(item => item.SK);
        setSelectedWPQData(skValuesArr);
      }
    }
    setLoading(false);
    setWiObj(result[0]);
  }

  useEffect(() => {
    if (action === "Edit") {
      setLoading(true);
      fetchNpcilByPKSK();
    }
    fetchProject();
    fetchColumnDefns();
  }, []);

  useEffect(() => {
    if (reload) {
      setReload(false);
    }
  }, [reload]); // Fetch widata and reset reload when reload changes

  const validate = async () => {
    let isError = false;
    let emptyFields = [];
    if (wiObj.WI_Project_No === "") {
      isError = true;
      emptyFields.push("Project No");
    }
    if (npcilflag) {
      if (wiObj.WI_Joint_No === "") {
        isError = true;
        emptyFields.push("Joint Number");
      }
    }
    if (wiObj.WI_Joint_Type === "") {
      isError = true;
      emptyFields.push("Joint Type");
    }
    if (wiObj.WI_Joint_Type === "Tubesheet") {
      if (wiObj.WI_Joint_No === "") {
        isError = true;
        emptyFields.push("Joint No");
      }
    }
    if (wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.Linked) {
      if (wiObj.WI_Welder_Id === "") {
        isError = true;
        emptyFields.push("Welder Id");
      }
    }
    if (isError) {
      showWarning(`Field Required: ${emptyFields.join(", ")}`);
    }
    setError((prevState) => ({
      ...prevState,
      ...error,
    }));
    if (error.WI_Project_No?.status) {
      isError = true;
    }
    return isError;
  };

  /**
   * CRITICAL BUSINESS LOGIC: WPQ-JOINT LINKING ALGORITHM
   * 
   * STEP 1: CONFLICT DETECTION
   * - Scans existing project combinations for joint overlaps
   * - Prevents same joint being assigned to multiple welders
   * - Shows confirmation modal for conflicting assignments
   * 
   * STEP 2: SMART GROUPING LOGIC (EXACT WELDER SET MATCHING)
   * - Groups joints ONLY when welder sets are IDENTICAL
   * - Comparison: selectedWPQData.length === eleWpqs.length && selectedWPQData.every(id => eleWpqs.includes(id))
   * - Example Scenarios:
   *   
   *   SCENARIO A - SUCCESSFUL MERGE:
   *   Existing: [W001#WPQ123, W002#WPQ456] → Groove → [J001, J002]
   *   New:      [W001#WPQ123, W002#WPQ456] → Groove → [J003, J004]
   *   Result:   [W001#WPQ123, W002#WPQ456] → Groove → [J001, J002, J003, J004]
   *   
   *   SCENARIO B - NO MERGE (DIFFERENT WELDER COUNT):
   *   Existing: [W001#WPQ123, W002#WPQ456] → Groove → [J001, J002]
   *   New:      [W001#WPQ123, W002#WPQ456, W003#WPQ789, W004#WPQ101] → Groove → [J003, J004]
   *   Result:   Two separate records (different welder team sizes)
   *   
   *   SCENARIO C - NO MERGE (DIFFERENT WELDERS):
   *   Existing: [W001#WPQ123, W002#WPQ456] → Groove → [J001, J002]
   *   New:      [W003#WPQ789, W004#WPQ101] → Groove → [J003, J004]
   *   Result:   Two separate records (completely different welders)
   * 
   * - Maintains referential integrity across operations
   * - Prevents mixing of different welder qualification combinations
   * 
   * STEP 3: DYNAMODB OPERATIONS WITH SEQUENCE MANAGEMENT
   * - Creates optimized SK pattern for efficient queries
   * - Handles cascade deletes for record updates
   * - Maintains sequence numbering for grouping
   * 
   * SEQUENCE NUMBER ASSIGNMENT LOGIC:
   * 
   * NEW RECORD CREATION:
   * - Calculate: maxSeqNo = Math.max(...existing.map(ele => ele.SeqNo || 1), 1)
   * - Assign: newSeqNo = maxSeqNo + 1
   * - Generate SK: `${projectNo}#${jointType}#SeqNo#${newSeqNo}`
   * 
   * MERGE INTO EXISTING SEQUENCE:
   * - Find compatible record with same welder set + joint type
   * - Reuse existing SeqNo and SK for consistency
   * - Merge joint numbers: [...existingJoints, ...newJoints]
   * - Delete old record, create consolidated record
   * 
   * SEQUENCE COLLISION PREVENTION:
   * - Each project+joint_type maintains independent sequence counters
   * - Concurrent operations use atomic increment operations
   * - Failed operations don't increment sequence numbers
   * 
   * SK PATTERN: "{ProjectNo}#{JointType}#SeqNo#{SequenceNumber}"
   * Examples:
   * - "NPCIL2024#Groove#SeqNo#1" (First Groove combination)
   * - "NPCIL2024#Groove#SeqNo#2" (Second Groove combination)
   * - "NPCIL2024#Overlay#SeqNo#1" (First Overlay combination)
   */
  const handleSubmit = async () => {
    try {
      if (!(await validate())) {
        wiObj.Action = action;

        // FETCH EXISTING COMBINATIONS FOR CONFLICT ANALYSIS
        let wpqJointCombinationsResult = await getWpqNpcilLinkByProjectNumber({ projectNumber: wiObj.WI_Project_No });
        setWpqJointCombinations(wpqJointCombinationsResult);

        // CALCULATE NEXT SEQUENCE NUMBER FOR NEW GROUPS
        let maxSeqNo = Math.max(...wpqJointCombinationsResult.map(ele => ele.SeqNo || 1), 1);
        let matchedEntry = null;
        let newObj = {};

        const jointNos = wiObj.WI_Joint_No.split(",");

        // STEP 1: CONFLICT DETECTION - Check for joint overlaps across different welders
        const { hasConflict } = await findAndHandleCommonJoints(wiObj, wpqJointCombinationsResult);
        if (hasConflict) {
          setShowConfirmModel(true);
          return;
        }

        // STEP 2: SMART GROUPING - Find compatible existing combinations
        for (let ele of wpqJointCombinationsResult) {
          const eleJoints = ele.WI_Joint_No.split(",");
          const eleWpqs = await getWPQLinkingData(ele);

          // CHECK WELDER COMPATIBILITY
          let isWelderMatch;
          if (selectedWPQData.length !== 0) {
            isWelderMatch = selectedWPQData.length === eleWpqs.length &&
              selectedWPQData.every(id => eleWpqs.includes(id));
          }
          else {
            isWelderMatch = true;
          }

          // FIND MATCHING COMBINATION FOR GROUPING
          if (isWelderMatch && ele.WI_Joint_Type === wiObj.WI_Joint_Type) {
            matchedEntry = { ...ele, eleJoints };
            break;
          }
        }

        // STEP 3: RECORD OPERATION LOGIC
        if (action === "Edit" && matchedEntry) {
          if (matchedEntry.SeqNo !== wiObj.SeqNo) {
            // MERGE JOINTS INTO EXISTING GROUP
            const jointsToAdd = matchedEntry.eleJoints.filter(joint => !jointNos.includes(joint));
            if (jointsToAdd.length > 0) {
              wiObj.WI_Joint_No = [...jointNos, ...jointsToAdd].join(',');
            }
            // CASCADE DELETE OLD RECORD
            await deleteNpcilWpqLink(matchedEntry.PK, matchedEntry.SK);
          }
          newObj = { ...wiObj };
        }
        else if (action === "Add") {
          if (matchedEntry && matchedEntry.SeqNo !== wiObj.SeqNo) {
            // MERGE INTO EXISTING SEQUENCE GROUP
            const jointsToAdd = matchedEntry.eleJoints.filter(joint => !jointNos.includes(joint));
            if (jointsToAdd.length > 0) {
              wiObj.WI_Joint_No = [...jointNos, ...jointsToAdd].join(',');
            }
            newObj = { ...wiObj, SeqNo: matchedEntry.SeqNo, SK: matchedEntry.SK };
          }
          else {
            // CREATE NEW SEQUENCE GROUP WITH OPTIMIZED SK
            newObj = {
              ...wiObj,
              SeqNo: maxSeqNo + 1,
              SK: `${wiObj.WI_Project_No}#${wiObj.WI_Joint_Type}#SeqNo#${maxSeqNo + 1}`
            };
          }
        }

        // FALLBACK FOR EDGE CASES
        if (Object.keys(newObj).length === 0 && newObj.constructor === Object) {
          newObj = { ...wiObj };
          if (newObj.SK === "" && wpqJointCombinationsResult.length === 0) {
            newObj.SeqNo = maxSeqNo;
            newObj.SK = wiObj.WI_Project_No + "#" + wiObj.WI_Joint_Type + "#SeqNo#" + maxSeqNo;
          }
        }

        // CLEANUP AND PERSIST TO DYNAMODB
        newObj.WI_WCS_Data = ""
        delete newObj.WI_WPQ_Data;
        let deleteLinksFlg = action === "Edit";

        /**
         * WPQ SAVING MECHANISM (TWO-PHASE COMMIT WITH CHUNKING):
         * 
         * PHASE 1: SAVE MAIN NPCIL LINKING RECORD
         * - addNpcilWpqLink(newObj, deleteLinksFlg)
         * - Creates/updates main linking record in WI_mst table
         * 
         * MAIN NPCIL RECORD STRUCTURE:
         * {
         *   PK: "NpcilWpqLinking",
         *   SK: "NPCIL2024#Groove#SeqNo#1",
         *   SeqNo: 1,
         *   WI_Project_No: "NPCIL2024",
         *   WI_Joint_No: "J001,J002,J003",
         *   WI_Joint_Type: "Groove",
         *   WI_Welder_Id: "W001,W002",  // Display only
         *   WI_Wpq_Radio: "Link WPQ with Project",
         *   Action: "Add",
         *   WI_WCS_Data: ""
         * }
         * 
         * PHASE 2: SAVE WPQ ASSOCIATIONS WITH CHUNKING
         * - addWPQLinkingData(selectedWPQData, newObj)
         * - Processes WPQ data in chunks of 200 records
         * - Creates multiple WPQ records to handle large datasets
         * - Prevents DynamoDB item size limits (400KB)
         * 
         * WPQ CHUNKING LOGIC:
         * - chunkSize = 200 WPQ records per chunk
         * - Each chunk creates separate record with incremented srNo
         * - SK pattern: "{MainSK}#WPQ#{ChunkNumber}"
         * 
         * WPQ RECORD STRUCTURE (PER CHUNK):
         * {
         *   PK: "NpcilWpqLinking",
         *   SK: "NPCIL2024#Groove#SeqNo#1#WPQ#1",  // Chunk 1
         *   WI_WPQ_Data: "W001#WPQ123,W002#WPQ456,..."  // Up to 200 WPQs
         * }
         * {
         *   PK: "NpcilWpqLinking",
         *   SK: "NPCIL2024#Groove#SeqNo#1#WPQ#2",  // Chunk 2
         *   WI_WPQ_Data: "W201#WPQ789,W202#WPQ101,..."  // Next 200 WPQs
         * }
         * 
         * CHUNKING BENEFITS:
         * - Handles unlimited WPQ associations per project
         * - Prevents DynamoDB item size limitations
         * - Enables efficient batch processing
         * - Supports parallel read/write operations
         * - Maintains query performance for large datasets
         * 
         * selectedWPQData format: ["W001#WPQ123", "W002#WPQ456", ...]
         * - Each element: WelderID#WPQRecordNumber
         * - Extracted from WPQ_HEADER table during selection
         * - Processed in chunks for storage optimization
         */
        let result = await addNpcilWpqLink(newObj, deleteLinksFlg);
        if (result.status) {
          await addWPQLinkingData(selectedWPQData, newObj);
          showSuccess(`Selected WPQ Linking has been ${action === "Edit" ? "Updated" : "Added"} successfully`);

          setReload(!reload);
          setShowForm(false);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleChangeselect = (e) => {
    const { name, value } = e.target;
    setWiObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const [popUp, setPopUp] = useState({
    name: "",
    type: "",
    key: "",
    value: "",
  });

  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  /**
   * WPQ DATA PROCESSING FROM POPUP SELECTION
   * 
   * INPUT: WPQ_HEADER records from popup selection
   * {
   *   WPQ_WelderId: "W001",
   *   WPQ_RecordNo: "WPQ123",
   *   WPQ_WelderName: "John Smith",
   *   ... other WPQ details
   * }
   * 
   * PROCESSING STEPS:
   * 1. Extract unique welder IDs for display (WI_Welder_Id field)
   * 2. Create SK format for linking: "WelderID#WPQRecordNo"
   * 3. Store in selectedWPQData for association table creation
   * 
   * OUTPUT FORMATS:
   * - skValuesArr: ["W001#WPQ123", "W002#WPQ456"] (for linking)
   * - welderValues: "W001,W002" (for UI display)
   * - filteredResult: Full WPQ records (for grid display)
   */
  const setPopUpData = (data) => {
    if (data.name === "WPQ") {
      setFilteredResult(data.result);
      let skValuesArr = [];
      let welderIdsArr = [];

      // PROCESS EACH SELECTED WPQ RECORD
      data.result.forEach(element => {
        // CREATE LINKING KEY: WelderID#WPQRecordNo
        skValuesArr.push(element.WPQ_WelderId + "#" + element.WPQ_RecordNo);

        // COLLECT UNIQUE WELDER IDS FOR DISPLAY
        if (!welderIdsArr.includes(element.WPQ_WelderId)) {
          welderIdsArr.push(element.WPQ_WelderId);
        }
      });

      let welderValues = welderIdsArr.join(",");
      setSelectedWPQData(skValuesArr);  // FOR ASSOCIATION TABLE CREATION
      setWiObj((prevState) => ({
        ...prevState,
        WI_Welder_Id: welderValues      // FOR UI DISPLAY ONLY
      }));
    }
    else if (data.name === "NPCIL") {
      if (wiObj.WI_Project_No && wiObj.WI_Joint_Type) {
        let arr = [];
        data.result.map((e) => {
          arr.push(e.WCS_Joint_No);
        });
        const newarr = arr.join(",");
        setWiObj((prevState) => ({
          ...prevState,
          WI_Joint_No: newarr
        }));
      }
      setShowForm(false);
    }
  };

  const fetchColumnDefns = async () => {
    const result = GlobalColumnDefinations(tmpcolumnDefs);
    setColumnDefs(result);
  };

  const handleCheckboxClick = (e) => {
    const selectedValue = e.target.value;
    setWiObj((prev) => {
      const updatedObj = {
        ...prev,
        WI_Wpq_Radio: selectedValue,
        WI_WPQ_Data: "",
        WI_Welder_Id: "",
      };
      if (selectedValue !== "Link WPQ with Project" && selectedValue !== "Linking WPQ with project not Required") {
        updatedObj.WI_Joint_Type = selectedValue;
        updatedObj.WI_Wpq_Radio = "Link WPQ with Project";
      }
      return updatedObj;
    });
  };

  const handleJointTypeChange = async (e) => {
    let value = e.value;
    setWiObj((prev) => ({
      ...prev,
      WI_Joint_Type: value,
      WI_Wpq_Radio:
        (value === "Tubesheet" || npcilflag)
          ? "Link WPQ with Project"
          : "Linking WPQ with project not Required",
      WI_Joint_No: "",
      WI_Welder_Id: ""
    }));

    setFilteredResult([]);
    setSelectedWPQData([]);
    wiObj.WI_WPQ_Data = "";
  }

  const handleProjectChange = async (e) => {
    let value = e.value;
    setWiObj((prev) => ({
      ...prev,
      WI_Project_No: value,
      WCS_Project_No: value,
      WI_Joint_No: "",
      WI_Joint_Type: "",
      ...(npcilflag && { WI_Wpq_Radio: "Link WPQ with Project" })
    }));
  }

  const handleCloseConf = () => {
    setShowConfirmModel(false);
  };


  const findAndHandleCommonJoints = async (wiObj, wpqJointCombinations) => {
    for (let ele of wpqJointCombinations) {
      const jointNos = wiObj.WI_Joint_No.split(",");
      const eleJoints = ele.WI_Joint_No.split(",");
      const commonJoints = jointNos.filter(joint => eleJoints.includes(joint));
      if (commonJoints.length > 0 && (ele.SeqNo !== wiObj.SeqNo || ele.WI_Joint_Type !== wiObj.WI_Joint_Type)) {
        return { hasConflict: true, conflictingEntry: ele };
      }
    }
    return { hasConflict: false };
  };


  const handleAddSubmit = async () => {
    let isWelderMatch = false;
    let deleteLinks = false;
    let matchedentry = wpqJointCombinations.filter(item => item.SeqNo === wiObj.SeqNo && item.WI_Joint_Type === wiObj.WI_Joint_Type);
    let jointNos = wiObj.WI_Joint_No.split(",");
    if (matchedentry?.length > 0) {
      let eleJoints = matchedentry[0].WI_Joint_No.split(",");
      let eleWpqs = await getWPQLinkingData(matchedentry[0]);

      const isExactJointMatch = jointNos.length === eleJoints.length &&
        jointNos.every(joint => eleJoints.includes(joint));
      const isExactLinkedWpqMatch = selectedWPQData.length === eleWpqs.length &&
        selectedWPQData.every(joint => eleWpqs.includes(joint));

      if (!isExactJointMatch && !isExactLinkedWpqMatch) {
        setShowConfirmModel(false);
        showWarning('Please change any one combination at a time (Joint Number or Linked WPQ).');
        return;
      }
    }
    for (let ele of wpqJointCombinations) {
      let eleJoints = ele.WI_Joint_No.split(",");
      const eleWpqs = await getWPQLinkingData(ele);

      let commonJoints;
      if (ele.SeqNo !== wiObj.SeqNo || (ele.SeqNo === wiObj.SeqNo && ele.WI_Joint_Type !== wiObj.WI_Joint_Type)) {
        commonJoints = jointNos.filter(joint => eleJoints.includes(joint));
      }
      if (commonJoints?.length > 0) {
        const isExactMatch = commonJoints.length === eleJoints.length &&
          commonJoints.every(joint => eleJoints.includes(joint));
        if (isExactMatch) {
          await deleteNpcilWpqLink(ele.PK, ele.SK);
        }
        else {
          let updatedEleJoints = eleJoints.filter(joint => !commonJoints.includes(joint));
          ele.WI_Joint_No = updatedEleJoints.join(",");
          await addNpcilWpqLink(ele, deleteLinks);
        }
      }
      isWelderMatch = selectedWPQData.length === eleWpqs.length &&
        selectedWPQData.every(id => eleWpqs.includes(id));
      if (isWelderMatch && wiObj.WI_Joint_Type === ele.WI_Joint_Type) {
        wiObj.SK = ele.SK;
        wiObj.SeqNo = ele.SeqNo;
        const jointsToAdd = eleJoints.filter(joint => !jointNos.includes(joint));
        wiObj.WI_Joint_No = [...jointNos, ...jointsToAdd].join(',');
      }
    }
    setShowConfirmModel(false);
    deleteLinks = true;
    let result = await addNpcilWpqLink(wiObj, deleteLinks);
    if (result.status) {
      let skArr = filteredResult.map(item => item.SK);
      await addWPQLinkingData(skArr, wiObj);
      showSuccess("Selected WPQ Linking has been Updated successfully");
    }
  }

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <ConfirmAlertModal
        action={action}
        handleYes={handleAddSubmit}
        formName={"NPCIL Form"}
        show={showConfirmModel}
        handleClose={handleCloseConf}
        backdrop="static"
        className="opacity-0 userMasterModal"
      />
      <div className="position-absolute ms-3" style={{ top: "15px" }}>
        <b style={{ fontSize: "22px" }}>{"NPCIL WPQ-Linking"}</b>
      </div>
      <div className="location">
        <div className="ltext" style={{ position: "relative", left: "7px" }}>
          {" "}
          Home
          {"> "}
          <span>{"Welder and Welding Operations"}</span> {"> "}
          <span>{"NPCIL WPQ-Linking"}</span>
        </div>
      </div>
      <Container fluid>
        <Row className="RowHeight">
          <Col md={4} sm={12} xs={12} className="ListTitle mt-2 mb-4"
            style={{ position: "relative", left: "7px" }}>
            Add New Project
          </Col>
        </Row>
        <div className="px-2 pb-2 mt-2 ">
          <Row className="gx-3" style={{ height: "40px" }}>
            <Col md={3}>
              <Form.Group className="promd4-input-selectNpcil">
                <div className="search-select">
                  <SearchSelect
                    id="WI_Project_No"
                    name="WI_Project_No"
                    data={projectDetails}
                    required
                    disabled={action === "Edit"}
                    valueField={"wld_ProjectNumber"}
                    value={wiObj.WI_Project_No}
                    labelField={"wld_ProjectNumber"}
                    placeholder=""
                    onChange={handleProjectChange}
                  ></SearchSelect>
                </div>
                <Form.Label>Project Number</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <div style={{ height: "14px" }}></div>
          <Row className="gx-3" style={{ height: "40px" }}>
            <Col md={3}>
              <Form.Group className="promd4-input-selectNpcil">
                <div className="search-select">
                  <SearchSelect
                    id="WI_Joint_Type"
                    name="WI_Joint_Type"
                    data={Jointstatus}
                    required
                    disabled={action === "Edit"}
                    valueField={"value"}
                    value={wiObj.WI_Joint_Type}
                    labelField={"name"}
                    placeholder=""
                    onChange={handleJointTypeChange}
                  ></SearchSelect>
                </div>
                <Form.Label>Joint Type</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <div style={{ height: "33px" }}></div>
          <Row className="gx-3" style={{ height: "40px" }}>
            <Col md={3} style={{ position: "relative", bottom: "20px" }}>
              <Form.Group className="g-input">
                <div className="d-flex align-items-center">
                  <Form.Control
                    id="WI_Joint_No"
                    type="text"
                    name="WI_Joint_No"
                    maxLength="100"
                    placeholder=" "
                    readOnly
                    value={wiObj.WI_Joint_No}
                    onChange={handleChangeselect}
                    className="rightPaddingNpcil"
                    onInput={(e) => {
                      let error = isAlphanumericWithDot(e.target.value);
                      setError((prevState) => ({
                        ...prevState,
                        SK: error
                      }));
                    }}
                  />
                  <Form.Label className="required-field" htmlFor="WI_Joint_No">
                    Select Joint Number
                  </Form.Label>
                  <BiSelectMultiple
                    size={20}
                    onClick={() => {
                      showPopUp("NPCIL",
                        "Multiple",
                        "WI_Joint_No", {
                        "projectNumber": wiObj.WI_Project_No,
                        "action": action
                      });
                    }}
                    color={"#707070"}
                    style={{ marginLeft: "-2rem" }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3" style={{ marginBottom: "2rem", height: "40px" }}>
            <Col md={2}>
              <div>
                <input
                  type="radio"
                  name="WI_Wpq_Radio"
                  value="Link WPQ with Project"
                  onClick={(e) => handleCheckboxClick(e)}
                  onChange={(e) => handleCheckboxClick(e)}
                  style={{ position: "relative", top: "2px" }}
                  checked={wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.Linked}
                />
                <span style={{ fontSize: "14px" }}>
                  &nbsp; Link WPQ with Project
                </span>
              </div>
            </Col>
            <Col md={3}>
              <div style={{ width: "200px", display: "inline-block", lineHeight: "1.2" }}>
                <input
                  disabled={wiObj.WI_Joint_Type === "Tubesheet" || npcilflag}
                  type="radio"
                  name="WI_Wpq_Radio"
                  value="Linking WPQ with project not Required"
                  onClick={(e) => handleCheckboxClick(e)}
                  onChange={(e) => handleCheckboxClick(e)}
                  style={{ position: "relative", top: "2px" }}
                  checked={wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.UnLinked}
                />
                <span style={{ fontSize: "14px" }}>
                  &nbsp; Linking WPQ with project
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    position: "relative",
                    left: "20px",
                  }}
                >
                  &nbsp; not Required
                </span>
              </div>
            </Col>
          </Row>
          <Row className="gx-3" style={{ marginBottom: "2rem" }}>
            <Col md={3} style={{ position: "relative", bottom: "20px" }}>
              <Form.Group className="g-input">
                <div className="d-flex align-items-center">
                  <Form.Control
                    id="WI_Welder_Id"
                    name="WI_Welder_Id"
                    type="text"
                    required
                    readOnly
                    disabled={wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.UnLinked}
                    placeholder=" "
                    className="rightPaddingNpcil"
                    color="var(--col-707070)"
                    value={wiObj.WI_Welder_Id}
                    onChange={handleChangeselect}
                    onInput={(e) => {
                      let error = isAlphanumericWithDot(e.target.value);
                      setError((prevState) => ({
                        ...prevState,
                        SK: error,
                      }));
                    }}
                  />
                  <Form.Label className="required-field" htmlFor="WI_Welder_Id">
                    Select WPQ
                  </Form.Label>
                  <BiSelectMultiple
                    size={20}
                    onClick={() => {
                      if (wiObj.WI_Wpq_Radio !== WPQ_Linking_Radio.UnLinked) {
                        if (!wiObj.WI_Joint_Type) {
                          showWarning("Please select Joint Type first");
                          return;
                        }
                        showPopUp(
                          "WPQ",
                          "Multiple",
                          "WI_Welder_Id",
                          wiObj.WI_Welder_Id
                        );
                      }
                    }}
                    color={"#707070"}
                    style={{ marginLeft: "-2rem" }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                onClick={handleSubmit}
                style={{ position: "relative", bottom: "30px" }}
                className="btn-add-color semiBoldToggleButton leftAlignedButton"
              >
                {action === "Edit" ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
          {filteredResult.length > 0 || action === Action.ActionEdit ? (
            <div
              style={{
                position: "relative",
                left: "500px",
                bottom: "420px",
                width: "580px",
                height: "483px",
                border: "1px solid var(--col-ddd)"
              }}
            >
              <GridMaster
                rowDetails={filteredResult}
                colDetails={columnDefs}
                fieldNames={fieldName}
                headerNames={headerName}
                ref={childRef}
                height={"99%"}
              />
            </div>
          ) : (
            ""
          )}
        </div>
        <RenderIf isTrue={popUp.type === "Multiple" && showForm}>
          <MultiValuePopUp
            show={showForm}
            onHide={() => setShowForm(false)}
            setSelected={setPopUpData}
            masterName={popUp.name}
            paramkey={popUp.key}
            paramvalue={popUp.value}
            backdrop="static"
            JointType={wiObj.WI_Joint_Type}
            wiobj={wiObj}
            filteredResult={filteredResult}
            action={action}
          />
        </RenderIf>
      </Container >

    </>
  );
});

export default WpqNpcilLinkingAddEdit;
